import React from "react";
import { Charges } from ".";
import { DisplayBox, Loading } from "../../components";
import { ApiHelper, DateHelper, InvoiceInterface } from "../../helpers";


interface Props {
  headerRef: React.RefObject<HTMLHeadingElement>;
}

export const Invoices: React.FC<Props> = (props) => {
  const [invoices, setInvoices] = React.useState<InvoiceInterface[]>(null);
  const [selectedInvoice, setSelectedInvoice] = React.useState<InvoiceInterface>(null);
  const [openYear, setOpenYear] = React.useState<number | null>(null);


  const init = () => {
    ApiHelper.get<InvoiceInterface[]>("/invoices").then((data) => setInvoices(data || []));
  };

  React.useEffect(init, []);

  const toggleYear = (year: number) => {
    setOpenYear((prev) => (prev === year ? null : year));
  };


  const groupInvoicesByYear = () => {
    const grouped: Record<number, InvoiceInterface[]> = {};
    invoices.forEach((invoice) => {
      const year = new Date(invoice.endDate).getFullYear();
      if (!grouped[year]) grouped[year] = [];
      grouped[year].push(invoice);
    });
    return grouped;
  };

  const getAccordionTable = () => {
    if (invoices === null) return <Loading />;
    if (invoices.length === 0) return <p>There are no invoices yet</p>;

    const formatter = Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
    const grouped = groupInvoicesByYear();

    return (
      <div className="space-y-4">
        {Object.keys(grouped)
          .sort((a, b) => Number(b) - Number(a))
          .map((yearStr) => {
            const year = Number(yearStr);
            const invoiceList = grouped[year];
            const isOpen = openYear === year;
            return (
              <div key={year} className="accordion mb-3" id={`accordion-${year}`}>
                <div className="accordion-item">
                  <h2 className="accordion-header" id={`heading-${year}`}>
                    <button
                      className={`btn w-100 d-flex justify-content-between align-items-center text-start border rounded px-3 py-2 
    bg-white text-dark shadow-sm transition  ${isOpen ? '' : 'collapsed'}`}
                      type="button"
                      onClick={() => toggleYear(year)}
                      aria-expanded={isOpen ? 'true' : 'false'}
                      aria-controls={`collapse-${year}`}
                    >
                      📁 {year}
                    </button>
                  </h2>
                  <div
                    id={`collapse-${year}`}
                    className={`accordion-collapse collapse ${isOpen ? 'show' : ''}`}
                    aria-labelledby={`heading-${year}`}
                    data-bs-parent={`#accordion-${year}`}
                  >
                    <div className="accordion-body p-0">
                      <table className="table table-striped table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th scope="col">Month</th>
                            <th scope="col">Amount</th>
                            <th scope="col">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {invoiceList.map((i, index) => (
                            <tr key={index}>
                              <td>
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    setSelectedInvoice(i);
                                    if (props.headerRef?.current) {
                                      const rect = props.headerRef.current.getBoundingClientRect();
                                      const scrollY = window.scrollY + rect.top;
                                      window.scrollTo({ top: scrollY - 20, behavior: "smooth" });
                                    }
                                  }}
                                  className="btn btn-link text-primary fw-semibold text-decoration-none p-0 border-0 bg-transparent text-start"
                                >
                                  📄 {DateHelper.formatDateTime(DateHelper.convertToDate(i.endDate), "MMMM yyyy")}
                                </button>
                              </td>
                              <td>{formatter.format(i.amount)}</td>
                              <td>{i.status}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>

            );
          })}
      </div>
    );
  };

  const getInvoiceDetails = () => {
    if (selectedInvoice) {
      return (
        <div>
          <Charges invoice={selectedInvoice} onClose={() => setSelectedInvoice(null)} />
        </div>
      );
    }
  };

  return (
    <>
      {getInvoiceDetails()}
      <DisplayBox headerText="Invoices" headerIcon="fas fa-credit-card">
        {getAccordionTable()}
      </DisplayBox>
    </>
  );
};
